import { useCalendarStore } from '@/store/calendarStore';
import { formatDate, formatTime, isSameDayAs, formatDuration, parseDateFromBackend } from '@/utils/dateUtils';
import { getDualDate, formatHebrewDate, getHebrewHolidays } from '@/utils/hebrewCalendar';
import { expandirReferencia } from '@/utils/bibliaHelper';
import { showExportDialog } from '@/utils/exportUtils';
import { FiBook, FiCalendar, FiStar, FiClock, FiDownload } from 'react-icons/fi';

/**
 * Vista de día del calendario
 */
export default function DayView() {
  const { currentDate, citas, lecturas, eventos, settings, filters } = useCalendarStore();
  
  console.log('📅 [DayView] ========== RENDERIZANDO DayView ==========');
  console.log('📅 [DayView] Fecha actual:', currentDate);
  console.log('📅 [DayView] 📚 Total lecturas en store:', lecturas?.length || 0);
  console.log('📅 [DayView] 📋 Total citas en store:', citas?.length || 0);
  console.log('📅 [DayView] 🎉 Total eventos en store:', eventos?.length || 0);
  console.log('📅 [DayView] 🔍 Filtros activos:', filters);

  // Manejo seguro de fechas y configuración
  let dualDate, holidays;
  try {
    dualDate = getDualDate(currentDate, settings?.hebrewDayStart || '18:00');
    holidays = filters.showFestividadesJudias ? getHebrewHolidays(currentDate) : [];
  } catch (error) {
    console.error('Error en DayView:', error);
    dualDate = { hebrew: { day: 1, monthName: '', hebrewYear: 5785 } };
    holidays = [];
  }

  // Filtrar items para el día actual con filtros aplicados
  const dayCitas = filters.showCitas ? (citas || []).filter(cita => {
    try {
      const citaDate = parseDateFromBackend(cita.fechaInicio);
      return isSameDayAs(citaDate, currentDate);
    } catch {
      return false;
    }
  }) : [];

  const dayLecturas = filters.showLecturas ? (lecturas || []).filter(lectura => {
    try {
      const lecturaDate = parseDateFromBackend(lectura.fecha);
      const isSameDay = isSameDayAs(lecturaDate, currentDate);
      
      // Aplicar filtro de tipos de lectura
      if (isSameDay && filters.tiposLectura.length > 0) {
        return filters.tiposLectura.includes(lectura.tipo);
      }
      
      return isSameDay;
    } catch {
      return false;
    }
  }) : [];

  const dayEventos = filters.showEventos ? (eventos || []).filter(evento => {
    try {
      const eventoDate = parseDateFromBackend(evento.fechaInicio);
      return isSameDayAs(eventoDate, currentDate);
    } catch {
      return false;
    }
  }) : [];

  console.log('📅 [DayView] 🎯 Filtrado completado:');
  console.log('📅 [DayView] 📋 Citas del día:', dayCitas.length);
  console.log('📅 [DayView] 📚 Lecturas del día:', dayLecturas.length);
  console.log('📅 [DayView] 🎉 Eventos del día:', dayEventos.length);
  if (dayLecturas.length > 0) {
    console.log('📅 [DayView] 📚 Muestra de lecturas:', dayLecturas.slice(0, 3));
  }

  // Agrupar lecturas por tipo para mejor visualización
  const lecturasAgrupadas = dayLecturas.reduce((acc, lectura) => {
    const tipo = lectura.tipo || 'Otros';
    if (!acc[tipo]) {
      acc[tipo] = [];
    }
    acc[tipo].push(lectura);
    return acc;
  }, {});

  const allItems = [
    ...dayCitas.map(c => ({ ...c, type: 'cita' })),
    ...dayLecturas.map(l => ({ ...l, type: 'lectura' })),
    ...dayEventos.map(e => ({ ...e, type: 'evento' })),
  ].sort((a, b) => {
    const aDate = parseDateFromBackend(a.fechaInicio || a.fecha);
    const bDate = parseDateFromBackend(b.fechaInicio || b.fecha);
    return aDate - bDate;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'cita': return <FiCalendar />;
      case 'lectura': return <FiBook />;
      case 'evento': return <FiStar />;
      default: return <FiCalendar />;
    }
  };

  // Contador total de eventos
  const totalEventos = dayCitas.length + dayLecturas.length + dayEventos.length;

  // Función para exportar
  const handleExport = () => {
    const daysData = [{
      date: currentDate,
      items: {
        citas: dayCitas,
        lecturas: dayLecturas,
        eventos: dayEventos
      }
    }];
    
    const title = `Calendario - Vista Día - ${formatDate(currentDate, 'dd/MM/yyyy')}`;
    showExportDialog(daysData, 'day', title);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 relative">
      {/* Botón de exportar */}
      <button
        onClick={handleExport}
        className="absolute top-4 right-4 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md"
        title="Exportar día"
      >
        <FiDownload className="w-4 h-4" />
        <span className="text-sm font-medium">Exportar</span>
      </button>

      <div className="mb-6">
        <h3 className="text-xl font-bold mb-2">
          {formatDate(currentDate, 'EEEE, d \'de\' MMMM \'de\' yyyy')}
        </h3>
        {settings?.showDualCalendar && dualDate?.hebrew && (
          <p className="text-sm text-gray-600 mb-2">
            {formatHebrewDate(dualDate.hebrew)}
          </p>
        )}
        {holidays && holidays.length > 0 && (
          <div className="mt-2 p-3 bg-accent bg-opacity-20 rounded-lg">
            <p className="text-sm font-semibold text-gray-700">Festividades:</p>
            {holidays.map((holiday, idx) => (
              <p key={idx} className="text-sm text-gray-600">• {holiday}</p>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4 pb-20">
        {allItems.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FiCalendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No hay eventos para este día</p>
            <p className="text-sm mt-2">
              {(!filters.showCitas && !filters.showLecturas && !filters.showEventos)
                ? 'Activa algún filtro para ver eventos'
                : 'Agrega una cita, lectura o evento'}
            </p>
          </div>
        ) : (
          <>
            {/* Lecturas agrupadas por tipo */}
            {Object.keys(lecturasAgrupadas).length > 0 && (
              <div className="space-y-3">
                {Object.entries(lecturasAgrupadas).map(([tipo, lecturasDelTipo]) => (
                  <div key={tipo} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FiBook className="w-4 h-4" />
                      {tipo} ({lecturasDelTipo.length})
                    </h4>
                    <div className="space-y-2">
                      {lecturasDelTipo.map((lectura, idx) => (
                        <div
                          key={`lectura-${lectura.id}-${idx}`}
                          className="border-l-4 p-3 rounded bg-white hover:shadow-sm transition-shadow"
                          style={{ borderLeftColor: lectura.color }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700">{lectura.subtipo}</p>
                              {lectura.referencia && (
                                <p className="text-sm font-semibold text-gray-800 mt-1">
                                  📚 {expandirReferencia(lectura.referencia)}
                                </p>
                              )}
                              {lectura.descripcion && (
                                <p className="text-xs text-gray-600 mt-1">{lectura.descripcion}</p>
                              )}
                            </div>
                            <span 
                              className="text-xs px-2 py-1 rounded-full ml-2"
                              style={{ backgroundColor: lectura.color, color: 'white' }}
                            >
                              {lectura.tipo}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Citas y Eventos */}
            {[...dayCitas.map(c => ({ ...c, type: 'cita' })), ...dayEventos.map(e => ({ ...e, type: 'evento' }))].map((item, idx) => (
              <div
                key={`${item.type}-${item.id}-${idx}`}
                className="border-l-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                style={{ borderLeftColor: item.color }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="p-2 rounded-lg mt-1"
                    style={{ backgroundColor: item.color, color: 'white' }}
                  >
                    {getIcon(item.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <h4 className="font-semibold text-gray-800">
                        {item.nombre || item.tipo}
                      </h4>
                      <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">
                        {item.type === 'cita' ? 'Cita' : 'Evento'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{item.descripcion}</p>
                    {item.fechaInicio && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <FiClock className="w-4 h-4" />
                        <span>
                          {formatTime(parseDateFromBackend(item.fechaInicio))}
                          {item.fechaFin && ` - ${formatTime(parseDateFromBackend(item.fechaFin))}`}
                          {item.duracion && ` (${formatDuration(item.duracion)})`}
                        </span>
                      </div>
                    )}
                    {item.cita && (
                      <p className="text-sm text-gray-500 mt-2 italic">
                        📖 {item.cita}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Contador de eventos en esquina inferior izquierda */}
      {totalEventos > 0 && (
        <div className="absolute bottom-4 left-4">
          <div className="bg-gray-700 text-white px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">Total eventos:</span>
              <span className="text-sm font-bold">{totalEventos}</span>
            </div>
            <div className="text-[10px] text-gray-300 mt-1">
              Citas: {dayCitas.length} | Lecturas: {dayLecturas.length} | Eventos: {dayEventos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



